'use client';

import { Button, Flex, Spinner, Text, Link } from '@chakra-ui/react';
import { AiOutlineUser } from 'react-icons/ai';
import { IoKeyOutline } from 'react-icons/io5';
import { FaChevronLeft, FaRegAddressCard } from 'react-icons/fa6';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { useCallback, useContext, useState } from 'react';
import { api } from '@/services/api';
import { ResponseError } from '@/types/Error';
import { AuthContext } from '@/contexts/AuthContext';
import { PasswordStrengthMeter } from '@/components/ui/password-input';
import FormCheckBox from '@/components/Form/form-checkbox';
import FormInput from '@/components/Form/form-input';
import ErrorMessage from '@/components/ErrorMessage';
import {
  GoogleReCaptcha,
  GoogleReCaptchaProvider,
} from 'react-google-recaptcha-v3';

type FormObject = {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  acceptedTerms: boolean;
  acceptedAdvertising?: boolean;
  recaptchaToken: string;
};

const schema = z
  .object({
    name: z
      .string({ message: 'Nome é obrigatório' })
      .min(1, { message: 'Nome é obrigatório' }),
    email: z
      .email({ message: 'E-mail inválido' })
      .min(1, { message: 'E-mail é obrigatório' }),
    password: z
      .string({ message: 'Palavra-passe é obrigatória' })
      .min(8, { message: 'A palavra-passe deve ter pelo menos 8 caracteres' })
      .regex(/[A-Z]/, {
        message: 'A palavra-passe deve conter pelo menos uma letra maiúscula',
      })
      .regex(/[a-z]/, {
        message: 'A palavra-passe deve conter pelo menos uma letra minúscula',
      })
      .regex(/[0-9]/, {
        message: 'A palavra-passe deve conter pelo menos um número',
      })
      .regex(/[^A-Za-z0-9]/, {
        message: 'A palavra-passe deve conter pelo menos um caractere especial',
      }),
    confirmPassword: z
      .string({ message: 'Confirmação da palavra-passe é obrigatória' })
      .min(1, { message: 'Confirmação da palavra-passe é obrigatória' }),
    acceptedTerms: z
      .boolean({ message: 'Aceitar os termos é obrigatório' })
      .refine((value) => value !== undefined, {
        message: 'Aceitar os termos é obrigatório',
      }),
    acceptedAdvertising: z.boolean().default(false),
    recaptchaToken: z.string().min(1, { message: 'reCAPTCHA é obrigatório' }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'As palavras-passe não coincidem',
    path: ['confirmPassword'],
  });

const UserCreateAccount = () => {
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { handleChangeStatus, handleSetConfirmStatus } =
    useContext(AuthContext);

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors },
  } = useForm<FormObject>({ resolver: zodResolver(schema) });

  const handlePasswordStrength = useCallback((password: string) => {
    if (!password) setPasswordStrength(0);
    let strength = 0;
    if (password.length >= 8) strength += 0.5;
    if (password.length >= 10) strength += 1;
    if (/[A-Z]/.test(password)) strength += 0.5;
    if (/[a-z]/.test(password)) strength += 0.5;
    if (/[0-9]/.test(password)) strength += 0.5;
    if (/[^A-Za-z0-9]/.test(password)) strength += 1;
    setPasswordStrength(strength);
  }, []);

  const onSubmit = useCallback(
    async (data: FormObject) => {
      setIsLoading(true);

      try {
        const response = await api.post('/customer-profile', {
          name: data.name,
          email: data.email,
          password: data.password,
          accepted_terms: data.acceptedTerms,
          accepted_advertising: data.acceptedAdvertising,
          recaptcha_token: data.recaptchaToken,
        });

        reset();
        setPasswordStrength(0);
        setErrorMessage('');

        handleSetConfirmStatus(response.data);
      } catch (error) {
        const e = error as ResponseError;

        setErrorMessage(
          e.response && e.response.data
            ? e.response.data.message
            : 'Error na comunicação com o servidor',
        );
      } finally {
        setIsLoading(false);
      }
    },
    [reset, handleSetConfirmStatus],
  );

  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || ''}
    >
      <Flex
        as="form"
        flexDir="column"
        gap="16px"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Flex align="center" gap="8px">
          <Flex
            as="button"
            onClick={() => handleChangeStatus('signin')}
            w="28px"
            h="28px"
            align="center"
            justify="center"
            borderRadius="8px"
            color={{ base: 'rgba(0,0,0,0.45)', _dark: 'rgba(255,255,255,0.4)' }}
            _hover={{ bg: 'rgba(0,176,202,0.08)', color: '#00B0CA' }}
            transition="all 0.15s"
            flexShrink={0}
          >
            <FaChevronLeft size={13} />
          </Flex>
          <Text
            fontSize="18px"
            fontWeight="700"
            color={{ base: '#0A1B2B', _dark: '#E2EFF5' }}
          >
            Criar Conta
          </Text>
        </Flex>
        <Flex>
          <FormInput
            placeholder="Nome e Apelido"
            width="100%"
            error={errors.name?.message}
            {...register('name')}
            icon={<FaRegAddressCard />}
          />
        </Flex>
        <Flex>
          <FormInput
            placeholder="E-mail"
            width="100%"
            error={errors.email?.message}
            {...register('email')}
            icon={<AiOutlineUser />}
          />
        </Flex>
        <Flex flexDir="column" gap="5px">
          <Controller
            control={control}
            name="password"
            render={({ field }) => (
              <FormInput
                placeholder="Palavra-passe"
                width="100%"
                error={errors.password?.message}
                onChange={(e) => {
                  field.onChange(e.currentTarget.value);
                  handlePasswordStrength(e.currentTarget.value);
                }}
                icon={<IoKeyOutline />}
                type="password"
              />
            )}
          />

          {passwordStrength > 0 && (
            <PasswordStrengthMeter value={passwordStrength} />
          )}
        </Flex>
        <Flex flexDir="column" gap="5px">
          <FormInput
            placeholder="Confirmar Palavra-passe"
            width="100%"
            error={errors.confirmPassword?.message}
            {...register('confirmPassword')}
            icon={<IoKeyOutline />}
            type="password"
          />
        </Flex>
        <Flex flexDir="column" gap="5px">
          <GoogleReCaptcha onVerify={(v) => setValue('recaptchaToken', v)} />
        </Flex>

        <Flex flexDir="column" gap="5px">
          <Controller
            control={control}
            name="acceptedAdvertising"
            render={({ field }) => (
              <FormCheckBox
                error={errors.acceptedAdvertising?.message}
                value={field.value || false}
                onChange={(data: boolean) => field.onChange(data)}
                width="100%"
              >
                <Text>
                  Gostaria de receber atualizações personalizadas e comunicações
                  comerciais por e-mail e outros canais.
                </Text>
              </FormCheckBox>
            )}
          />
        </Flex>
        <Flex flexDir="column" gap="5px">
          <Controller
            control={control}
            name="acceptedTerms"
            render={({ field }) => (
              <FormCheckBox
                error={errors.acceptedTerms?.message}
                value={field.value}
                onChange={(data: boolean) => field.onChange(data)}
                width="100%"
              >
                <Text>
                  Confirmo que li e aceito os{' '}
                  <Link
                    href="/condicoes-gerais-venda"
                    color="#00B0CA"
                    textDecoration="underline"
                  >
                    Termos e Condições
                  </Link>
                  , bem como o uso dos meus dados pessoais conforme descrito na
                  <Link
                    href="/Informacoes/PoliticasPrivacidade"
                    color="#00B0CA"
                    textDecoration="underline"
                  >
                    {' '}
                    Política de Privacidade
                  </Link>
                  .
                </Text>
              </FormCheckBox>
            )}
          />
        </Flex>
        <Flex>
          <Button
            type="submit"
            h="44px"
            w="full"
            borderRadius="full"
            bg="#00B0CA"
            color="white"
            fontWeight="700"
            fontSize="15px"
            boxShadow="0 4px 20px rgba(0,176,202,0.3)"
            disabled={isLoading}
            _hover={{ bg: '#009AB5' }}
            _disabled={{ opacity: 0.5 }}
            transition="all 0.2s"
          >
            {isLoading ? <Spinner size="sm" color="white" /> : 'Criar Conta'}
          </Button>
        </Flex>
        {errorMessage && <ErrorMessage message={errorMessage} />}
        {errors.recaptchaToken && (
          <ErrorMessage message={errors.recaptchaToken.message || ''} />
        )}
      </Flex>
    </GoogleReCaptchaProvider>
  );
};

export default UserCreateAccount;
